import { useEffect, useState } from "react";
import QRCode from "qrcode";

/** Renders a real QR for the given URL. Falls back to a pseudo-random
 *  pattern (same visual weight) when value is undefined. */
export function QrPlaceholder({
  size = 140,
  value,
}: {
  size?: number;
  value?: string;
}) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setDataUrl(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(value, {
      width: size,
      margin: 1,
      color: { dark: "#0F1115", light: "#ffffff" },
    })
      .then((u) => {
        if (!cancelled) setDataUrl(u);
      })
      .catch(() => {
        if (!cancelled) setDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  if (value && dataUrl) {
    return (
      <img
        src={dataUrl}
        alt={`QR code for ${value}`}
        width={size}
        height={size}
        style={{ background: "#fff", borderRadius: 8 }}
      />
    );
  }

  const cells = 21;
  const cell = size / cells;
  const blocks: Array<[number, number]> = [];
  let seed = 1729;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      if (rand() > 0.55) blocks.push([x, y]);
    }
  }
  const finders: Array<[number, number]> = [
    [0, 0],
    [cells - 7, 0],
    [0, cells - 7],
  ];

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ background: "#fff", borderRadius: 8 }}
      aria-label="QR code (placeholder)"
    >
      {blocks.map(([x, y], i) => (
        <rect key={i} x={x * cell} y={y * cell} width={cell} height={cell} fill="#0F1115" />
      ))}
      {finders.map(([fx, fy], i) => (
        <g key={`f${i}`}>
          <rect x={fx * cell} y={fy * cell} width={cell * 7} height={cell * 7} fill="#fff" />
          <rect x={fx * cell} y={fy * cell} width={cell * 7} height={cell * 7} fill="#0F1115" />
          <rect x={(fx + 1) * cell} y={(fy + 1) * cell} width={cell * 5} height={cell * 5} fill="#fff" />
          <rect x={(fx + 2) * cell} y={(fy + 2) * cell} width={cell * 3} height={cell * 3} fill="#0F1115" />
        </g>
      ))}
    </svg>
  );
}
